import { api } from './api';

/** Tipos da collection `achievement` da Funifier. */
export const ACHIEVEMENT_TYPE = {
  POINT: 0,
  CHALLENGE: 1,
  CATALOG_ITEM: 2,
  LEVEL: 3,
};

/** Quantas conquistas mais recentes formam o corpo do feed. */
const FEED_SIZE = 50;

async function aggregateAchievements(pipeline: any[]): Promise<any[]> {
  const { data } = await api.post(`database/achievement/aggregate`, pipeline);
  return Array.isArray(data) ? data : [];
}

/** As conquistas mais recentes do jogador, de qualquer tipo. */
function getLatestAchievements(player: string) {
  return aggregateAchievements([{ $match: { player } }, { $sort: { time: -1 } }, { $limit: FEED_SIZE }]);
}

/**
 * A conquista mais recente de CADA desafio já concluído pelo jogador.
 *
 * Sem esta consulta, desafios raros nunca chegam ao feed: cada evento gera um
 * par de conquistas (o desafio e os pontos dele), então os de alta frequência
 * — acesso diário, consumo de conteúdo, conclusão de aula — soterram os
 * demais. Na base de produção, "Completar Curso" aparecia entre as posições 49
 * e 239 do histórico dos jogadores que concluíram um curso, e essa posição só
 * cresce com o tempo: nenhum `$limit` fixo resolve o problema de forma estável.
 */
function getLatestAchievementPerChallenge(player: string) {
  return aggregateAchievements([
    { $match: { player, type: ACHIEVEMENT_TYPE.CHALLENGE } },
    { $sort: { time: -1 } },
    { $group: { _id: '$item', achievement: { $first: '$$ROOT' } } },
    { $replaceRoot: { newRoot: '$achievement' } },
  ]);
}

function dedupeById(achievements: any[]): any[] {
  const seen: Record<string, boolean> = {};

  return achievements.filter(achievement => {
    if (seen[achievement._id]) {
      return false;
    }

    seen[achievement._id] = true;
    return true;
  });
}

/**
 * Feed de conquistas recentes: as últimas conquistas do jogador, acrescidas do
 * último registro de cada desafio concluído, para que marcos raros apareçam.
 */
export async function getPlayerAchievements(player: string): Promise<any[]> {
  const [latest, perChallenge] = await Promise.all([
    getLatestAchievements(player),
    getLatestAchievementPerChallenge(player),
  ]);

  return dedupeById(latest.concat(perChallenge)).sort((a, b) => b.time - a.time);
}
